/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { JSX, ReactElement } from "react";
import { forwardRef, Fragment } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPercentages, spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { IconElement } from "../../icons/Icon/Icon";
import { ChevronRightIcon } from "../../icons/Icon/NavigationIcons";
import { Avatar } from "../Avatar/Avatar.emotion";
import { baseStyle, contentStyle, disabledStyle } from "../listStyles.emotion";
import { Skeleton } from "../Skeleton/Skeleton.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const metadataStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

const loadingBaseStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.ms,
  paddingBottom: spacing.s,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.s,
  width: numericPercentages[100],
};

const loadingContentStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.xs,
};

const loadingDescriptionStyle: Interpolation = {
  height: spacing.m,
};

const loadingTitleStyle: Interpolation = {
  height: 20,
};

export type ListProfileProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  avatar?: ReactElement<any>;
  description?: string;
  disabled?: boolean;
  icon?: IconElement;
  loading?: boolean;
  metadata?: string;
  title: string;
};

export const ListProfile = forwardRef<any, ListProfileProps>(
  (
    {
      asChild = false,
      avatar,
      children,
      description,
      disabled,
      icon = <ChevronRightIcon />,
      loading = false,
      metadata,
      title,
      ...props
    },
    ref,
  ) => {
    if (loading)
      return (
        <div ref={ref} css={loadingBaseStyle} {...props}>
          <Avatar loading size={64} />
          <div css={loadingContentStyle}>
            <Skeleton css={loadingTitleStyle} />
            <Skeleton css={loadingDescriptionStyle} />
          </div>
        </div>
      );

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        avatar,
        <span key="content" css={contentStyle}>
          <Text overflow="ellipsis" size="m" type="title">
            {title}
          </Text>
          {description && (
            <Text overflow="ellipsis" size="s" type="body">
              {description}
            </Text>
          )}
          {metadata && (
            <Text
              overflow="ellipsis"
              size="l"
              css={metadataStyle}
              type="detail"
            >
              {metadata}
            </Text>
          )}
        </span>,
        <Fragment key="icon">{icon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[baseStyle, disabled && disabledStyle]}
        {...{ disabled, ...props }}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListProfile.displayName = "ListProfile";
